import { GoogleMap, Marker } from '@react-google-maps/api';
import { MapPin } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@/components/ui/input';
import {
  clientPointToLatLng,
  DEFAULT_MAP_CENTER,
  formatLocationDisplay,
  GOOGLE_MAPS_API_KEY,
  hasLocationCoordinates,
  latLngToClientPoint,
  parseCoord,
  placeResultToLocation,
  reverseGeocode,
} from '@/lib/google-maps';
import { createGoogleMapPinIcon } from '@/lib/map-pin-icon';
import { useGoogleMapsLoader } from './useGoogleMapsLoader';

const mapContainerStyle = {
  height: '400px',
  width: '100%',
  borderRadius: '20px',
};

const AUTOCOMPLETE_FIELDS = [
  'address_components',
  'geometry',
  'name',
  'formatted_address',
];

const PALETTE_DROP_PADDING_PX = 8;
const MARKER_MOUNT_DELAY_MS = 100;

function isPointerOverElement(element, clientX, clientY, padding = 0) {
  if (!element) {
    return false;
  }
  const rect = element.getBoundingClientRect();
  return (
    clientX >= rect.left - padding &&
    clientX <= rect.right + padding &&
    clientY >= rect.top - padding &&
    clientY <= rect.bottom + padding
  );
}

export function InteractiveMap({
  location,
  locationName = '',
  onLocationChange,
  onLocationNameChange,
  readOnly = false,
  showSearch = false,
  geocodeOnPlace = false,
  defaultCenter = DEFAULT_MAP_CENTER,
  className = '',
  hintText,
}) {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const mapWrapperRef = useRef(null);
  const paletteRef = useRef(null);
  const paletteButtonRef = useRef(null);
  const isOverPaletteRef = useRef(false);
  const isDraggingMarkerRef = useRef(false);
  const isDraggingFromPaletteRef = useRef(false);
  const isPlacingFromPaletteRef = useRef(false);
  const paletteDragOverMapRef = useRef(false);
  const paletteDragFinishedRef = useRef(false);
  const activePalettePointerIdRef = useRef(null);
  const hadCoordinatesRef = useRef(false);
  const paletteDragCleanupRef = useRef(null);
  const finishPaletteDragRef = useRef(null);
  const cancelPaletteDragRef = useRef(null);
  const updatePaletteDragTargetRef = useRef(null);
  const locationRef = useRef(location);
  const mapViewportRef = useRef({ center: defaultCenter, zoom: 12 });
  const geocodeRequestRef = useRef(0);
  const [mapViewport, setMapViewport] = useState(() => mapViewportRef.current);
  const [showMapMarker, setShowMapMarker] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState(null);
  const [isDraggingMarker, setIsDraggingMarker] = useState(false);
  const [markerDragPosition, setMarkerDragPosition] = useState(null);
  const [isOverMap, setIsOverMap] = useState(false);
  const [isOverPalette, setIsOverPalette] = useState(false);

  const markerCenter = useMemo(
    () => ({
      lat: parseCoord(location?.latitude, defaultCenter.lat),
      lng: parseCoord(location?.longitude, defaultCenter.lng),
    }),
    [
      location?.latitude,
      location?.longitude,
      defaultCenter.lat,
      defaultCenter.lng,
    ],
  );

  locationRef.current = location;

  const hasCoordinates = hasLocationCoordinates(location);
  const mapCenter = hasCoordinates ? markerCenter : mapViewport.center;
  const mapZoom = hasCoordinates ? 17 : mapViewport.zoom;
  const canDragFromPalette = !readOnly && !hasCoordinates;

  const markerIcon = useMemo(() => {
    if (!isLoaded || typeof google?.maps?.Size !== 'function') {
      return undefined;
    }
    return createGoogleMapPinIcon();
  }, [isLoaded]);

  const resolvedHint =
    hintText ??
    (readOnly
      ? 'Location preview.'
      : showSearch
        ? 'Search for a place, drag the pin onto the map, or drag it back to the tray to clear.'
        : 'Drag the pin onto the map to set the location, or drag it back to the tray to clear.');

  const setLocationDisplay = useCallback(
    (displayName) => {
      if (inputRef.current) {
        inputRef.current.value = displayName;
      }
      onLocationNameChange?.(displayName);
    },
    [onLocationNameChange],
  );

  const applyLocation = useCallback(
    (nextLocation, displayName) => {
      if (displayName) {
        setLocationDisplay(displayName);
        onLocationChange?.(nextLocation, displayName);
        return;
      }
      onLocationChange?.(nextLocation);
    },
    [onLocationChange, setLocationDisplay],
  );

  const captureMapViewport = useCallback(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    const centerLatLng = map.getCenter();
    const zoom = map.getZoom();
    if (!centerLatLng || zoom == null) {
      return;
    }

    const nextViewport = {
      center: { lat: centerLatLng.lat(), lng: centerLatLng.lng() },
      zoom,
    };
    mapViewportRef.current = nextViewport;
    setMapViewport(nextViewport);
  }, []);

  const clearLocation = useCallback(() => {
    geocodeRequestRef.current += 1;
    setIsGeocoding(false);
    setShowMapMarker(false);
    captureMapViewport();

    const clearedLocation = {
      ...(locationRef.current ?? {}),
      latitude: undefined,
      longitude: undefined,
      streetAddress: undefined,
      city: undefined,
      state: undefined,
      zipcode: undefined,
      formattedAddress: undefined,
      locationName: undefined,
    };

    if (showSearch || onLocationNameChange) {
      setLocationDisplay('');
    }
    onLocationChange?.(clearedLocation);
  }, [
    captureMapViewport,
    onLocationChange,
    onLocationNameChange,
    setLocationDisplay,
    showSearch,
  ]);

  const resolveCoordinates = useCallback(
    async (lat, lng) => {
      const baseLocation = {
        ...(locationRef.current ?? {}),
        latitude: lat,
        longitude: lng,
      };

      if (!geocodeOnPlace) {
        onLocationChange?.(baseLocation);
        return;
      }

      const requestId = geocodeRequestRef.current + 1;
      geocodeRequestRef.current = requestId;
      setIsGeocoding(true);
      onLocationChange?.({ latitude: lat, longitude: lng });

      try {
        const resolved = await reverseGeocode(lat, lng);
        if (geocodeRequestRef.current !== requestId) {
          return;
        }
        const displayName = formatLocationDisplay(resolved);
        applyLocation(resolved, displayName);
      } catch {
        if (geocodeRequestRef.current === requestId) {
          onLocationChange?.({ latitude: lat, longitude: lng });
        }
      } finally {
        if (geocodeRequestRef.current === requestId) {
          setIsGeocoding(false);
        }
      }
    },
    [applyLocation, geocodeOnPlace, onLocationChange],
  );

  const waitForMapIdle = useCallback((map) => {
    return new Promise((resolve) => {
      if (!map) {
        resolve();
        return;
      }

      let settled = false;
      const finish = () => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(fallbackTimer);
        resolve();
      };

      google.maps.event.addListenerOnce(map, 'idle', finish);
      const fallbackTimer = window.setTimeout(finish, 200);
    });
  }, []);

  const placeCoordinates = useCallback(
    async (clientX, clientY) => {
      const map = mapRef.current;
      if (!map) {
        return;
      }

      await waitForMapIdle(map);
      const latLng = await clientPointToLatLng(map, clientX, clientY);
      await resolveCoordinates(latLng.lat(), latLng.lng());
    },
    [resolveCoordinates, waitForMapIdle],
  );

  const handlePlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();
    if (!place) {
      return;
    }

    const nextLocation = placeResultToLocation(place);
    if (!nextLocation) {
      return;
    }

    const displayName =
      formatLocationDisplay(nextLocation) ||
      place.formatted_address ||
      place.name ||
      '';
    applyLocation(nextLocation, displayName);
  }, [applyLocation]);

  const isOverPaletteAtPoint = useCallback((clientX, clientY) => {
    return isPointerOverElement(
      paletteRef.current,
      clientX,
      clientY,
      PALETTE_DROP_PADDING_PX,
    );
  }, []);

  const isOverMapAtPoint = useCallback(
    (clientX, clientY) => {
      if (isOverPaletteAtPoint(clientX, clientY)) {
        return false;
      }

      const mapDiv = mapRef.current?.getDiv();
      if (mapDiv && isPointerOverElement(mapDiv, clientX, clientY)) {
        return true;
      }

      return isPointerOverElement(mapWrapperRef.current, clientX, clientY);
    },
    [isOverPaletteAtPoint],
  );

  const updatePaletteDragTarget = useCallback(
    (clientX, clientY) => {
      const overMap = isOverMapAtPoint(clientX, clientY);
      paletteDragOverMapRef.current = overMap;
      setIsOverMap(overMap);
      return overMap;
    },
    [isOverMapAtPoint],
  );

  const releasePalettePointerCapture = useCallback((pointerId) => {
    const button = paletteButtonRef.current;
    if (button?.hasPointerCapture(pointerId)) {
      button.releasePointerCapture(pointerId);
    }
  }, []);

  const removePaletteDragListeners = useCallback(() => {
    const listeners = paletteDragCleanupRef.current;
    if (!listeners) {
      return;
    }

    window.removeEventListener('pointermove', listeners.onMove, true);
    window.removeEventListener('pointerup', listeners.onUp, true);
    window.removeEventListener('pointercancel', listeners.onCancel, true);
    paletteDragCleanupRef.current = null;
    activePalettePointerIdRef.current = null;
  }, []);

  const resetPaletteDragUi = useCallback(() => {
    isDraggingFromPaletteRef.current = false;
    paletteDragOverMapRef.current = false;
    setIsDragging(false);
    setDragPosition(null);
    setIsOverMap(false);
  }, []);

  const finishPaletteDrag = useCallback(
    async (clientX, clientY, pointerId) => {
      if (!isDraggingFromPaletteRef.current || paletteDragFinishedRef.current) {
        return;
      }

      paletteDragFinishedRef.current = true;
      removePaletteDragListeners();
      if (pointerId != null) {
        releasePalettePointerCapture(pointerId);
      }
      resetPaletteDragUi();

      const overMapAtDrop = isOverMapAtPoint(clientX, clientY);
      const overMap = paletteDragOverMapRef.current || overMapAtDrop;
      if (!overMap) {
        return;
      }

      isPlacingFromPaletteRef.current = true;
      try {
        await placeCoordinates(clientX, clientY);
      } finally {
        isPlacingFromPaletteRef.current = false;
      }
    },
    [
      isOverMapAtPoint,
      placeCoordinates,
      releasePalettePointerCapture,
      removePaletteDragListeners,
      resetPaletteDragUi,
    ],
  );

  const cancelPaletteDrag = useCallback(
    (pointerId) => {
      if (!isDraggingFromPaletteRef.current) {
        return;
      }

      removePaletteDragListeners();
      if (pointerId != null) {
        releasePalettePointerCapture(pointerId);
      }
      resetPaletteDragUi();
    },
    [
      releasePalettePointerCapture,
      removePaletteDragListeners,
      resetPaletteDragUi,
    ],
  );

  finishPaletteDragRef.current = finishPaletteDrag;
  cancelPaletteDragRef.current = cancelPaletteDrag;
  updatePaletteDragTargetRef.current = updatePaletteDragTarget;

  const updatePaletteDropTarget = useCallback(
    (clientX, clientY) => {
      const overPalette = isOverPaletteAtPoint(clientX, clientY);
      isOverPaletteRef.current = overPalette;
      setIsOverPalette(overPalette);
      return overPalette;
    },
    [isOverPaletteAtPoint],
  );

  const markerDroppedOnPalette = useCallback(
    (event) => {
      if (isOverPaletteRef.current) {
        return true;
      }

      const domEvent = event.domEvent;
      return Boolean(
        domEvent && isOverPaletteAtPoint(domEvent.clientX, domEvent.clientY),
      );
    },
    [isOverPaletteAtPoint],
  );

  const handleMarkerDragStart = useCallback(
    (event) => {
      if (readOnly || !showMapMarker) {
        return;
      }
      isDraggingMarkerRef.current = true;
      isOverPaletteRef.current = false;
      setIsOverPalette(false);
      setIsDraggingMarker(true);

      const domEvent = event.domEvent;
      if (domEvent) {
        setMarkerDragPosition({ x: domEvent.clientX, y: domEvent.clientY });
      }
    },
    [readOnly, showMapMarker],
  );

  const handleMarkerDrag = useCallback(
    (event) => {
      if (readOnly || !isDraggingMarkerRef.current) {
        return;
      }

      const domEvent = event.domEvent;
      if (domEvent) {
        setMarkerDragPosition({ x: domEvent.clientX, y: domEvent.clientY });
        updatePaletteDropTarget(domEvent.clientX, domEvent.clientY);
        return;
      }

      const map = mapRef.current;
      if (!map || !event.latLng) {
        return;
      }

      latLngToClientPoint(map, event.latLng)
        .then((point) => {
          setMarkerDragPosition(point);
          updatePaletteDropTarget(point.x, point.y);
        })
        .catch(() => {});
    },
    [readOnly, updatePaletteDropTarget],
  );

  const handleMarkerDragEnd = useCallback(
    async (event) => {
      try {
        if (
          readOnly ||
          !showMapMarker ||
          !event.latLng ||
          !isDraggingMarkerRef.current ||
          isPlacingFromPaletteRef.current
        ) {
          return;
        }

        const droppedOnPalette = markerDroppedOnPalette(event);
        isOverPaletteRef.current = false;
        setIsOverPalette(false);

        if (droppedOnPalette) {
          clearLocation();
          return;
        }

        await resolveCoordinates(event.latLng.lat(), event.latLng.lng());
      } finally {
        isDraggingMarkerRef.current = false;
        setIsDraggingMarker(false);
        setMarkerDragPosition(null);
      }
    },
    [
      clearLocation,
      markerDroppedOnPalette,
      readOnly,
      resolveCoordinates,
      showMapMarker,
    ],
  );

  const handlePalettePointerDown = useCallback(
    (event) => {
      if (!canDragFromPalette || !mapRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      removePaletteDragListeners();
      paletteDragFinishedRef.current = false;
      isOverPaletteRef.current = false;
      setIsOverPalette(false);
      isPlacingFromPaletteRef.current = false;
      activePalettePointerIdRef.current = event.pointerId;
      isDraggingFromPaletteRef.current = true;
      paletteDragOverMapRef.current = isOverMapAtPoint(
        event.clientX,
        event.clientY,
      );
      setIsDragging(true);
      setDragPosition({ x: event.clientX, y: event.clientY });
      setIsOverMap(paletteDragOverMapRef.current);

      const pointerId = event.pointerId;
      try {
        event.currentTarget.setPointerCapture(pointerId);
      } catch {
        // Pointer capture is best-effort; window listeners handle the drop.
      }

      const onMove = (moveEvent) => {
        if (
          !isDraggingFromPaletteRef.current ||
          moveEvent.pointerId !== pointerId
        ) {
          return;
        }
        setDragPosition({ x: moveEvent.clientX, y: moveEvent.clientY });
        updatePaletteDragTargetRef.current?.(
          moveEvent.clientX,
          moveEvent.clientY,
        );
      };

      const onUp = (upEvent) => {
        if (upEvent.pointerId !== pointerId) {
          return;
        }
        finishPaletteDragRef.current?.(
          upEvent.clientX,
          upEvent.clientY,
          pointerId,
        );
      };

      const onCancel = (cancelEvent) => {
        if (cancelEvent.pointerId !== pointerId) {
          return;
        }
        cancelPaletteDragRef.current?.(pointerId);
      };

      window.addEventListener('pointermove', onMove, true);
      window.addEventListener('pointerup', onUp, true);
      window.addEventListener('pointercancel', onCancel, true);
      paletteDragCleanupRef.current = { onMove, onUp, onCancel };
    },
    [canDragFromPalette, isOverMapAtPoint, removePaletteDragListeners],
  );

  useEffect(() => {
    if (
      showSearch &&
      inputRef.current &&
      inputRef.current.value !== locationName
    ) {
      inputRef.current.value = locationName;
    }
  }, [locationName, showSearch]);

  useEffect(() => {
    if (
      !isLoaded ||
      readOnly ||
      !showSearch ||
      !inputRef.current ||
      !google?.maps?.places
    ) {
      return undefined;
    }

    const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
      fields: AUTOCOMPLETE_FIELDS,
    });
    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener(
      'place_changed',
      handlePlaceChanged,
    );

    return () => {
      if (listener) listener.remove();
      autocompleteRef.current = null;
      google.maps.event.clearInstanceListeners(autocomplete);
    };
  }, [handlePlaceChanged, isLoaded, readOnly, showSearch]);

  useEffect(() => {
    return () => {
      removePaletteDragListeners();
    };
  }, [removePaletteDragListeners]);

  useEffect(() => {
    if (!hasCoordinates) {
      setShowMapMarker(false);
      hadCoordinatesRef.current = false;
      return undefined;
    }

    const justPlaced = !hadCoordinatesRef.current;
    hadCoordinatesRef.current = true;

    const markerTimer = window.setTimeout(
      () => {
        setShowMapMarker(true);
      },
      justPlaced ? MARKER_MOUNT_DELAY_MS : 0,
    );

    if (mapRef.current) {
      mapRef.current.panTo(markerCenter);
      const nextViewport = { center: markerCenter, zoom: 17 };
      mapViewportRef.current = nextViewport;
      setMapViewport(nextViewport);
    }

    return () => {
      window.clearTimeout(markerTimer);
    };
  }, [hasCoordinates, markerCenter.lat, markerCenter.lng]);

  if (loadError) {
    return (
      <div className={`interactive-map ${className}`.trim()}>
        <div style={{ ...mapContainerStyle, padding: '1rem' }}>
          Map could not be loaded. Check your Google Maps API key and network
          connection.
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={`interactive-map ${className}`.trim()}>
        <div style={mapContainerStyle} />
      </div>
    );
  }

  if (!GOOGLE_MAPS_API_KEY?.trim()) {
    return (
      <div className={`interactive-map ${className}`.trim()}>
        <div style={{ ...mapContainerStyle, padding: '1rem' }}>
          Set <code>VITE_GOOGLE_MAPS_API_KEY</code> in your environment to
          enable the map.
        </div>
      </div>
    );
  }

  return (
    <div className={`interactive-map ${className}`.trim()}>
      {showSearch ? (
        <Input
          ref={inputRef}
          type="text"
          defaultValue={locationName}
          placeholder="Enter address or venue name..."
          onChange={(event) => onLocationNameChange?.(event.target.value)}
          disabled={readOnly}
          autoComplete="off"
          aria-busy={isGeocoding}
        />
      ) : null}
      {showSearch || !readOnly ? (
        <p className="interactive-map-hint">{resolvedHint}</p>
      ) : null}
      <div
        ref={mapWrapperRef}
        className={`interactive-map-wrapper${isOverMap && isDragging ? ' interactive-map-wrapper--drop-target' : ''}`}
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={mapZoom}
          center={mapCenter}
          onLoad={(map) => {
            mapRef.current = map;
            const centerLatLng = map.getCenter();
            const zoom = map.getZoom();
            if (centerLatLng && zoom != null) {
              const nextViewport = {
                center: { lat: centerLatLng.lat(), lng: centerLatLng.lng() },
                zoom,
              };
              mapViewportRef.current = nextViewport;
              setMapViewport(nextViewport);
            }
          }}
          onIdle={captureMapViewport}
          onUnmount={() => {
            mapRef.current = null;
          }}
          options={{ clickableIcons: !readOnly, streetViewControl: false }}
        >
          {showMapMarker && hasCoordinates ? (
            <Marker
              position={markerCenter}
              draggable={!readOnly}
              opacity={isDraggingMarker ? 0 : 1}
              onDragStart={handleMarkerDragStart}
              onDrag={handleMarkerDrag}
              onDragEnd={handleMarkerDragEnd}
              icon={markerIcon}
            />
          ) : null}
        </GoogleMap>
        {!readOnly ? (
          <div
            ref={paletteRef}
            className={`interactive-map-palette${isOverPalette ? ' interactive-map-palette--drop-target' : ''}`}
            aria-label="Drop pin here to clear location"
          >
            <button
              ref={paletteButtonRef}
              type="button"
              className={`interactive-map-palette-pin${canDragFromPalette ? '' : ' interactive-map-palette-pin--inactive'}`}
              aria-label="Drag to place location on map"
              aria-hidden={!canDragFromPalette && !isDragging}
              tabIndex={canDragFromPalette ? 0 : -1}
              onPointerDown={handlePalettePointerDown}
              onPointerUp={(event) => {
                if (
                  !isDraggingFromPaletteRef.current ||
                  event.pointerId !== activePalettePointerIdRef.current
                ) {
                  return;
                }
                finishPaletteDragRef.current?.(
                  event.clientX,
                  event.clientY,
                  event.pointerId,
                );
              }}
              onPointerCancel={(event) => {
                if (event.pointerId !== activePalettePointerIdRef.current) {
                  return;
                }
                cancelPaletteDragRef.current?.(event.pointerId);
              }}
            >
              {canDragFromPalette && !isDragging ? (
                <MapPin aria-hidden="true" />
              ) : null}
            </button>
          </div>
        ) : null}
      </div>
      {(isDragging && dragPosition) || (isDraggingMarker && markerDragPosition)
        ? createPortal(
            <div
              className={`interactive-map-drag-ghost${
                isDraggingMarker
                  ? isOverPalette
                    ? ' interactive-map-drag-ghost--over-palette'
                    : ''
                  : isOverMap
                    ? ' interactive-map-drag-ghost--over-map'
                    : ''
              }`}
              style={{
                left: (isDraggingMarker ? markerDragPosition : dragPosition).x,
                top: (isDraggingMarker ? markerDragPosition : dragPosition).y,
              }}
              aria-hidden="true"
            >
              <MapPin />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
